(() => {
  if (window.ProhealthCartUnlockedGifts) return;

  const MODULE = '[data-ph-unlock-gifts]';
  const CARD = '[data-ph-gift-card]';
  const CAROUSEL = '[data-ph-gift-carousel]';
  const GIFT_PROPERTY = '_ph_unlock_gift';
  const MAX_TIERS = 6;

  let reconcileTimer = null;
  let checkoutBypass = false;
  let operationQueue = Promise.resolve();

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const queueOperation = (operation) => {
    const queued = operationQueue.then(operation, operation);
    operationQueue = queued.catch(() => undefined);
    return queued;
  };

  const scrollToElementWhenReady = (selector, attempts = 16) => {
    const target = document.querySelector(selector);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (attempts > 0) {
      window.setTimeout(() => scrollToElementWhenReady(selector, attempts - 1), 120);
    }
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to retrieve the latest cart.');
    return response.json();
  };

  const giftItems = (cart) =>
    (cart?.items || []).filter((item) => item.properties?.[GIFT_PROPERTY] === 'true');

  // cart.total_price includes line-level and cart-level discounts. Subtracting
  // marked gift-line value leaves the discounted paid-merchandise subtotal.
  const paidSubtotal = (cart) => {
    const giftLineValue = giftItems(cart).reduce(
      (total, item) => total + Number(item.final_line_price || 0),
      0
    );
    return Math.max(Number(cart?.total_price || 0) - giftLineValue, 0);
  };

  const getReferenceModule = (context = document) =>
    context?.querySelector?.(MODULE) || document.querySelector(MODULE);

  const parseVariantIds = (value) =>
    new Set(
      String(value || '')
        .split('|')
        .map((variantId) => Number(variantId))
        .filter((variantId) => Number.isFinite(variantId) && variantId > 0)
    );

  const getTierConfig = (module) => {
    if (!module) return [];

    const tiers = [];
    let previousThreshold = 0;

    for (let index = 1; index <= MAX_TIERS; index += 1) {
      const threshold = Number(module.getAttribute(`data-ph-tier-${index}-threshold`) || 0);
      const variantIds = parseVariantIds(module.getAttribute(`data-ph-tier-${index}-variants`));

      if (threshold > previousThreshold && variantIds.size > 0) {
        tiers.push({ index, threshold, variantIds });
        previousThreshold = threshold;
      }
    }

    return tiers;
  };

  const getUnlockedTier = (tiers, subtotal) => {
    let unlocked = null;
    tiers.forEach((tier) => {
      if (subtotal >= tier.threshold) unlocked = tier;
    });
    return unlocked;
  };

  const includesPreviousTiers = (module) =>
    module?.dataset?.includePreviousTiers === 'true';

  const requiresGiftSelection = (module) =>
    module?.dataset?.requireGiftSelection !== 'false';

  const isTierAllowed = (tier, unlockedTier, includePrevious) => {
    if (!tier || !unlockedTier) return false;
    return includePrevious ? tier.index <= unlockedTier.index : tier.index === unlockedTier.index;
  };

  const getItemTier = (item, tiers) => {
    const itemTierIndex = Number(item?.properties?._ph_unlock_tier || 0);
    const itemThreshold = Number(item?.properties?._ph_unlock_threshold || 0);
    return tiers.find(
      (tier) => tier.index === itemTierIndex && tier.threshold === itemThreshold
    ) || null;
  };

  const giftMatchesAllowedTier = (item, tiers, unlockedTier, includePrevious) => {
    const itemTier = getItemTier(item, tiers);
    if (!item || !itemTier || !isTierAllowed(itemTier, unlockedTier, includePrevious)) return false;

    return (
      Number(item.quantity) === 1 &&
      Number(item.final_line_price || 0) === 0 &&
      itemTier.variantIds.has(Number(item.variant_id))
    );
  };

  const updateCartLines = async (updates) => {
    if (!Object.keys(updates).length) return getCart();

    const response = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) throw new Error('Unable to update the gift in the cart.');
    return response.json();
  };

  const removeGiftItems = async (items, exceptKey = '') => {
    const updates = {};
    items.forEach((item) => {
      if (item.key !== exceptKey) updates[item.key] = 0;
    });
    return Object.keys(updates).length ? updateCartLines(updates) : null;
  };

  const refreshThemeCart = () => {
    const drawer = document.querySelector('m-cart-drawer');
    const page = document.querySelector('m-cart');

    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      drawer.onCartDrawerUpdate(true);
    }
    if (page && typeof page.onCartUpdate === 'function') {
      page.onCartUpdate(true);
    }
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        source: 'prohealth-unlocked-gift'
      });
    }
  };

  const notify = (module, message, type = 'warning') => {
    if (!message) return;
    if (window.MinimogTheme?.Notification?.show) {
      window.MinimogTheme.Notification.show({
        target: module || document.body,
        type,
        message
      });
    } else {
      window.alert(message);
    }
  };

  const validateCart = async ({ cart: suppliedCart = null, module: suppliedModule = null } = {}) => {
    const module = suppliedModule || getReferenceModule();
    let cart = suppliedCart || await getCart();
    const tiers = getTierConfig(module);
    const includePrevious = includesPreviousTiers(module);

    let subtotal = paidSubtotal(cart);
    let unlockedTier = getUnlockedTier(tiers, subtotal);
    let gifts = giftItems(cart);
    let validGift = gifts.find((item) =>
      giftMatchesAllowedTier(item, tiers, unlockedTier, includePrevious)
    ) || null;
    let changed = false;

    const invalidOrDuplicateGifts = gifts.filter((item) => item.key !== validGift?.key);
    if (invalidOrDuplicateGifts.length) {
      cart = await removeGiftItems(gifts, validGift?.key || '') || cart;
      changed = true;
    }

    // Cart-level discount allocation can change after removing a line. Re-read
    // eligibility from the updated cart response and validate the retained gift.
    subtotal = paidSubtotal(cart);
    unlockedTier = getUnlockedTier(tiers, subtotal);
    gifts = giftItems(cart);
    validGift = gifts.find((item) =>
      giftMatchesAllowedTier(item, tiers, unlockedTier, includePrevious)
    ) || null;

    if (gifts.length && !validGift) {
      cart = await removeGiftItems(gifts) || cart;
      changed = true;
      subtotal = paidSubtotal(cart);
      unlockedTier = getUnlockedTier(tiers, subtotal);
      gifts = giftItems(cart);
      validGift = null;
    }

    return {
      cart,
      changed,
      subtotal,
      unlockedTier,
      validGift,
      missingRequiredGift: Boolean(
        requiresGiftSelection(module) && unlockedTier && !validGift
      )
    };
  };

  const reconcileNow = (options = {}) =>
    queueOperation(async () => {
      const module = options.module || getReferenceModule();
      if (!module) return { cart: options.cart || null, changed: false };

      try {
        const result = await validateCart({ cart: options.cart, module });
        if (result.changed || options.refresh) refreshThemeCart();
        return result;
      } catch (error) {
        console.error('Prohealth unlocked gift validation:', error);
        if (options.notifyOnError) notify(module, error.message, 'error');
        throw error;
      }
    });

  const scheduleReconcile = () => {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      reconcileNow().catch(() => undefined);
    }, 180);
  };

  const selectGift = (card) =>
    queueOperation(async () => {
      if (card.disabled) return;

      const module = card.closest(MODULE);
      const variantId = Number(card.dataset.variantId);
      const cardTierIndex = Number(card.dataset.tier);
      const cardThreshold = Number(card.dataset.threshold);
      if (!module || !variantId || !cardTierIndex || !cardThreshold) return;

      module.classList.add('is-loading');

      try {
        let cart = await getCart();
        const existingGifts = giftItems(cart);
        if (existingGifts.length) {
          cart = await removeGiftItems(existingGifts) || cart;
        }

        const tiers = getTierConfig(module);
        const unlockedTier = getUnlockedTier(tiers, paidSubtotal(cart));
        const includePrevious = includesPreviousTiers(module);
        const selectedTier = tiers.find(
          (tier) => tier.index === cardTierIndex && tier.threshold === cardThreshold
        );
        const isAllowedVariant = Boolean(
          selectedTier &&
          unlockedTier &&
          isTierAllowed(selectedTier, unlockedTier, includePrevious) &&
          selectedTier.variantIds.has(variantId)
        );

        if (!isAllowedVariant) {
          throw new Error('Your cart total has changed. Please select a gift from an available tier.');
        }

        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _ph_unlock_gift: 'true',
                _ph_unlock_tier: String(selectedTier.index),
                _ph_unlock_threshold: String(selectedTier.threshold)
              }
            }]
          })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add this gift.');
        }

        cart = await getCart();
        const result = await validateCart({ cart, module });
        const addedGift = result.validGift;

        if (!addedGift || Number(addedGift.variant_id) !== variantId) {
          throw new Error(
            module.dataset.errorMessage ||
            'The gift could not be validated as FREE and was removed from your cart.'
          );
        }

        refreshThemeCart();
        window.setTimeout(
          () => scrollToElementWhenReady(`[data-cart-item][data-product-id="${addedGift.product_id}"]`),
          320
        );
      } catch (error) {
        notify(module, error.message);
        refreshThemeCart();
      } finally {
        module.classList.remove('is-loading');
      }
    });

  const updateScrollReference = (module) => {
    const carousel = module.querySelector(CAROUSEL);
    const thumb = module.querySelector('[data-ph-gift-scroll-thumb]');
    const track = module.querySelector('[data-ph-gift-scroll-track]');
    const arrow = module.querySelector('[data-ph-gift-next]');
    if (!carousel) return;

    const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
    const hasOverflow = maxScroll > 4;

    if (arrow) arrow.hidden = !hasOverflow;
    if (track) track.hidden = !hasOverflow;
    if (!thumb || !hasOverflow) return;

    const visibleRatio = Math.min(carousel.clientWidth / carousel.scrollWidth, 1);
    const thumbWidth = Math.max(visibleRatio * 100, 18);
    const travel = 100 - thumbWidth;
    const progress = carousel.scrollLeft / maxScroll;

    thumb.style.width = `${thumbWidth}%`;
    thumb.style.transform = `translateX(${progress * travel / (thumbWidth / 100)}%)`;
  };

  const scrollCarousel = (button) => {
    const module = button.closest(MODULE);
    const carousel = module?.querySelector(CAROUSEL);
    const card = carousel?.querySelector(CARD);
    if (!carousel || !card) return;

    const step = card.getBoundingClientRect().width + 10;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const atEnd = carousel.scrollLeft >= maxScroll - 4;

    carousel.scrollTo({
      left: atEnd ? 0 : Math.min(carousel.scrollLeft + step, maxScroll),
      behavior: 'smooth'
    });
  };

  const bindScrollReferenceDrag = (module) => {
    const carousel = module.querySelector(CAROUSEL);
    const track = module.querySelector('[data-ph-gift-scroll-track]');
    const thumb = module.querySelector('[data-ph-gift-scroll-thumb]');
    if (!carousel || !track || !thumb || track.dataset.phGiftDragBound === 'true') return;

    track.dataset.phGiftDragBound = 'true';

    const scrollFromPointer = (clientX) => {
      const rect = track.getBoundingClientRect();
      const thumbWidth = thumb.getBoundingClientRect().width;
      const available = Math.max(rect.width - thumbWidth, 1);
      const thumbLeft = Math.min(Math.max(clientX - rect.left - (thumbWidth / 2), 0), available);
      const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
      carousel.scrollLeft = (thumbLeft / available) * maxScroll;
    };

    const stopDragging = (event) => {
      if (track.dataset.phGiftDragging !== 'true') return;
      track.dataset.phGiftDragging = 'false';
      track.classList.remove('is-dragging');
      if (event?.pointerId !== undefined && track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      track.dataset.phGiftDragging = 'true';
      track.classList.add('is-dragging');
      track.setPointerCapture?.(event.pointerId);
      scrollFromPointer(event.clientX);
    });

    track.addEventListener('pointermove', (event) => {
      if (track.dataset.phGiftDragging === 'true') scrollFromPointer(event.clientX);
    });
    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
  };

  const bindModule = (module) => {
    if (module.dataset.phGiftBound === 'true') return;
    module.dataset.phGiftBound = 'true';

    const carousel = module.querySelector(CAROUSEL);
    bindScrollReferenceDrag(module);

    if (carousel) {
      carousel.addEventListener('scroll', () => updateScrollReference(module), { passive: true });
      window.requestAnimationFrame(() => updateScrollReference(module));

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(() => updateScrollReference(module));
        resizeObserver.observe(carousel);
      } else {
        window.addEventListener('resize', () => updateScrollReference(module), { passive: true });
      }
    }

    module.addEventListener('click', (event) => {
      const card = event.target.closest(CARD);
      if (card) {
        event.preventDefault();
        selectGift(card).catch(() => undefined);
        return;
      }

      const next = event.target.closest('[data-ph-gift-next]');
      if (next) scrollCarousel(next);
    });
  };

  const continueCheckout = (control) => {
    checkoutBypass = true;

    if (control instanceof HTMLAnchorElement) {
      window.location.assign(control.href);
      return;
    }

    control.disabled = false;
    control.click();
    window.setTimeout(() => {
      checkoutBypass = false;
    }, 0);
  };

  const guardCheckout = async (control) => {
    const context = control.closest?.('m-cart, m-cart-drawer, form') || document;
    const module = getReferenceModule(context);
    if (!module) {
      continueCheckout(control);
      return;
    }

    const canDisable = 'disabled' in control;
    const wasDisabled = canDisable ? control.disabled : false;
    if (canDisable) control.disabled = true;
    control.setAttribute('aria-busy', 'true');

    try {
      const result = await reconcileNow({ module, notifyOnError: true });

      if (result.missingRequiredGift) {
        const message = module.dataset.checkoutMessage || 'Please select a free gift to continue.';
        notify(module, message, 'warning');
        window.setTimeout(() => scrollToElementWhenReady(MODULE), 100);
        return;
      }

      continueCheckout(control);
    } catch (error) {
      notify(module, 'Your cart could not be verified. Please try again.', 'error');
    } finally {
      control.removeAttribute('aria-busy');
      if (!checkoutBypass && canDisable) control.disabled = wasDisabled;
    }
  };

  const bindCheckoutGuard = () => {
    if (document.documentElement.dataset.phGiftCheckoutBound === 'true') return;
    document.documentElement.dataset.phGiftCheckoutBound = 'true';

    document.addEventListener('click', (event) => {
      const control = event.target.closest(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      );
      if (!control || checkoutBypass || control.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(control);
    }, true);

    document.addEventListener('submit', (event) => {
      if (checkoutBypass) return;
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || submitter.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(submitter);
    }, true);
  };

  const init = () => {
    document.querySelectorAll(MODULE).forEach(bindModule);
    bindCheckoutGuard();
    scheduleReconcile();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => {
      window.setTimeout(init, 80);
    });
  }

  const observer = new MutationObserver(() => {
    document.querySelectorAll(MODULE).forEach(bindModule);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.ProhealthCartUnlockedGifts = {
    init,
    reconcile: scheduleReconcile,
    reconcileNow,
    afterCartMutation: (cart) => reconcileNow({ cart, refresh: true, notifyOnError: true }),
    getPaidSubtotal: paidSubtotal
  };
})();
